import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { conditionGroupSummary, calculateRowReach, CONDITION_FIELDS, SENDER_TOKENS, CHANNELS, type WaterfallRow } from './waterfallTypes';
import type { CampaignMember } from './types';

function senderLabel(sender: WaterfallRow['sender']): string {
  if (!sender) return '';
  if (sender.type === 'token') {
    const t = SENDER_TOKENS.find(t => t.value === sender.token);
    return t ? `Sender: ${t.label}` : '';
  }
  return sender.email ? `Sender: ${sender.email}` : '';
}

function PreviewRow({ row, reach }: { row: WaterfallRow; reach: number }) {
  const [expanded, setExpanded] = useState(false);

  const sender = senderLabel(row.sender);
  const enabledCells = Object.values(row.cells).filter(c => c.enabled);
  const allReady = enabledCells.length > 0 && enabledCells.every(c => c.copy_status === 'ready');

  return (
    <div className="bg-lemon-50 dark:bg-lemon-950/30 ring-1 ring-lemon-200 dark:ring-lemon-800 rounded-2xl overflow-hidden">
      {/* Header — clickable */}
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-lemon-100/60 dark:hover:bg-lemon-900/20 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="w-3.5 h-3.5 flex-shrink-0" />

        <span className="text-lemon-500 dark:text-lemon-600 flex-shrink-0">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>

        <span className="text-sm font-semibold text-nightshade-800 dark:text-nightshade-200 w-28 flex-shrink-0 truncate">
          {row.name || <span className="italic text-nightshade-400 font-normal">Unnamed</span>}
        </span>

        <span className="text-xs text-nightshade-500 dark:text-nightshade-400 flex-1 truncate min-w-0">
          {conditionGroupSummary(row.condition_groups, row.group_logic)}
        </span>

        {sender && (
          <span className="text-xs text-nightshade-400 dark:text-nightshade-500 flex-shrink-0 hidden lg:block">{sender}</span>
        )}

        <span className="text-xs font-semibold text-nightshade-500 dark:text-nightshade-400 tabular-nums flex-shrink-0">{reach}</span>

        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border flex-shrink-0 ${
          allReady
            ? 'bg-matcha-50 dark:bg-matcha-900/20 text-matcha-700 dark:text-matcha-400 border-matcha-200 dark:border-matcha-800'
            : 'bg-tangerine-50 dark:bg-tangerine-900/20 text-tangerine-700 dark:text-tangerine-400 border-tangerine-200 dark:border-tangerine-800'
        }`}>
          {allReady ? 'Ready' : 'Draft'}
        </span>
      </div>

      {/* Expanded read-only details */}
      {expanded && (
        <div className="border-t border-lemon-200 dark:border-lemon-800 px-5 py-4 space-y-4 bg-lemon-50/80 dark:bg-lemon-950/10">
          {/* Conditions */}
          {(row.condition_groups ?? []).length > 0 && (
            <div>
              <p className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider mb-2">
                Conditions{row.condition_groups.length > 1 ? ` (groups joined by ${row.group_logic})` : ` (${row.condition_groups[0]?.logic})`}
              </p>
              <div className="space-y-2">
                {row.condition_groups.map((group, gi) => (
                  <div key={group.id ?? gi} className={row.condition_groups.length > 1 ? 'pl-2 border-l-2 border-nightshade-200 dark:border-nightshade-700' : ''}>
                    {row.condition_groups.length > 1 && (
                      <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-1 font-medium">Group {gi + 1} ({group.logic})</p>
                    )}
                    <div className="space-y-1">
                      {group.conditions.map((c, i) => {
                        const f = CONDITION_FIELDS.find(f => f.field === c.field);
                        const label = f ? `${f.object} · ${f.label}` : c.field;
                        const op = c.operator === 'is_not_null' ? '≠ null'
                          : c.operator === 'is_null' ? '= null'
                          : c.operator === 'equals' ? `= ${c.value ?? ''}`
                          : c.operator === 'not_equals' ? `≠ ${c.value ?? ''}`
                          : `${c.operator} ${c.value ?? ''}`;
                        return (
                          <div key={i} className="text-xs text-nightshade-600 dark:text-nightshade-400">
                            <span className="text-nightshade-400">{label}</span> {op}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Follow-up description */}
          {row.follow_up_description && (
            <div>
              <p className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider mb-1">Follow-up</p>
              <p className="text-xs text-nightshade-600 dark:text-nightshade-400">{row.follow_up_description}</p>
            </div>
          )}

          {/* Cells */}
          <div>
            <p className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider mb-2">Flow IDs & Copy</p>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(Object.keys(row.cells).length, 3)}, minmax(0, 1fr))` }}>
              {Object.entries(row.cells).map(([col, cell]) => {
                const channelLabel = CHANNELS.find(ch => ch.value === cell.channel)?.label ?? cell.channel;
                return (
                  <div
                    key={col}
                    className={`rounded-lg border p-2.5 ${
                      cell.enabled
                        ? 'border-lemon-200 dark:border-lemon-800 bg-white/60 dark:bg-lemon-950/20'
                        : 'border-nightshade-100 dark:border-nightshade-800 bg-nightshade-50 dark:bg-nightshade-800/20 opacity-50'
                    }`}
                  >
                    <p className="text-xs font-semibold text-nightshade-700 dark:text-nightshade-300 mb-1.5">{col}</p>
                    {cell.enabled ? (
                      <div className="space-y-1">
                        <p className="text-xs text-nightshade-500 dark:text-nightshade-400">{channelLabel}</p>
                        {cell.flow_id && (
                          <p className="text-xs font-mono text-nightshade-400 truncate" title={cell.flow_id}>{cell.flow_id}</p>
                        )}
                        <span className={`inline-flex text-xs px-1.5 py-0.5 rounded-full font-medium ${
                          cell.copy_status === 'ready'
                            ? 'bg-matcha-50 text-matcha-700 dark:bg-matcha-900/20 dark:text-matcha-400'
                            : 'bg-tangerine-50 text-tangerine-700 dark:bg-tangerine-900/20 dark:text-tangerine-400'
                        }`}>
                          {cell.copy_status === 'ready' ? 'Ready' : 'Draft'}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-nightshade-400 italic">Disabled</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface WaterfallPreviewProps {
  rows: WaterfallRow[];
  members: CampaignMember[];
  templateName: string;
}

export function WaterfallPreview({ rows, members, templateName }: WaterfallPreviewProps) {
  const rowReach = useMemo(() => calculateRowReach(members, rows), [members, rows]);

  return (
    <div className="rounded-2xl border-2 border-lemon-300 dark:border-lemon-700 overflow-hidden">
      {/* Section header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-lemon-100/60 dark:bg-lemon-900/20 border-b border-lemon-200 dark:border-lemon-800">
        <span className="text-xs font-bold text-lemon-700 dark:text-lemon-400 uppercase tracking-widest">
          Template preview
        </span>
        <span className="text-xs text-lemon-600 dark:text-lemon-500 font-medium">{templateName}</span>
      </div>

      <div className="p-2 space-y-1.5 bg-lemon-50/40 dark:bg-lemon-950/10">
        {rows.map(row => (
          <PreviewRow key={row.id} row={row} reach={rowReach[row.id] ?? 0} />
        ))}
      </div>
    </div>
  );
}
